import { useCallback, useEffect, useMemo, useReducer } from "react";
import type { SSOFormState } from "../types";
import { hasErrors, validateSSOForm } from "../services/sso-validation";
import type { UpdateOrganizationSsoInput } from "../../../graphql/generated";

type Action =
  | { type: "RESET"; payload: SSOFormState }
  | { type: "SET_ENABLED"; value: boolean }
  | { type: "SET_ENFORCED"; value: boolean }
  | { type: "ADD_EMAIL_DOMAIN"; value: string }
  | { type: "REMOVE_EMAIL_DOMAIN"; index: number }
  | { type: "SET_GOOGLE_ENABLED"; value: boolean }
  | { type: "SET_GOOGLE_HOSTED_DOMAIN"; value: string }
  | { type: "SET_GOOGLE_ALLOW_JIT"; value: boolean }
  | { type: "SET_MICROSOFT_ENABLED"; value: boolean }
  | { type: "SET_MICROSOFT_TENANT_ID"; value: string }
  | { type: "SET_MICROSOFT_ALLOW_JIT"; value: boolean };

function reducer(state: SSOFormState, action: Action): SSOFormState {
  switch (action.type) {
    case "RESET":
      return action.payload;
    case "SET_ENABLED": {
      // If disabling SSO, also drop enforcement (mirrors server rule 2).
      const enforced = action.value ? state.enforced : false;
      return { ...state, enabled: action.value, enforced };
    }
    case "SET_ENFORCED":
      return { ...state, enforced: action.value };
    case "ADD_EMAIL_DOMAIN": {
      const next = action.value.trim().toLowerCase();
      if (!next || state.emailDomains.includes(next)) return state;
      if (state.emailDomains.length >= 10) return state;
      return { ...state, emailDomains: [...state.emailDomains, next] };
    }
    case "REMOVE_EMAIL_DOMAIN":
      return {
        ...state,
        emailDomains: state.emailDomains.filter((_, i) => i !== action.index),
      };
    case "SET_GOOGLE_ENABLED":
      return { ...state, google: { ...state.google, enabled: action.value } };
    case "SET_GOOGLE_HOSTED_DOMAIN":
      return {
        ...state,
        google: { ...state.google, hostedDomain: action.value },
      };
    case "SET_GOOGLE_ALLOW_JIT":
      return { ...state, google: { ...state.google, allowJIT: action.value } };
    case "SET_MICROSOFT_ENABLED":
      return {
        ...state,
        microsoft: { ...state.microsoft, enabled: action.value },
      };
    case "SET_MICROSOFT_TENANT_ID":
      return {
        ...state,
        microsoft: { ...state.microsoft, tenantId: action.value },
      };
    case "SET_MICROSOFT_ALLOW_JIT":
      return {
        ...state,
        microsoft: { ...state.microsoft, allowJIT: action.value },
      };
    default:
      return state;
  }
}

function deepEqual(a: SSOFormState, b: SSOFormState): boolean {
  if (a.enabled !== b.enabled) return false;
  if (a.enforced !== b.enforced) return false;
  if (a.emailDomains.length !== b.emailDomains.length) return false;
  for (let i = 0; i < a.emailDomains.length; i += 1) {
    if (a.emailDomains[i] !== b.emailDomains[i]) return false;
  }
  if (a.google.enabled !== b.google.enabled) return false;
  if (a.google.hostedDomain !== b.google.hostedDomain) return false;
  if (a.google.allowJIT !== b.google.allowJIT) return false;
  if (a.microsoft.enabled !== b.microsoft.enabled) return false;
  if (a.microsoft.tenantId !== b.microsoft.tenantId) return false;
  if (a.microsoft.allowJIT !== b.microsoft.allowJIT) return false;
  return true;
}

export interface UseSSOForm {
  state: SSOFormState;
  errors: ReturnType<typeof validateSSOForm>;
  isDirty: boolean;
  isValid: boolean;
  dispatch: React.Dispatch<Action>;
  toMutationInput: (organizationId: string) => UpdateOrganizationSsoInput;
}

/**
 * Local form state for the SSO settings page.
 * - Resets when the server payload changes (after a save / refetch).
 * - Mirrors server rules so the Save button is gated client-side.
 */
export function useSSOForm(initial: SSOFormState): UseSSOForm {
  const [state, dispatch] = useReducer(reducer, initial);

  // Keep the form synchronised with fresh server data.
  useEffect(() => {
    dispatch({ type: "RESET", payload: initial });
  }, [initial]);

  const errors = useMemo(() => validateSSOForm(state), [state]);
  const isDirty = useMemo(() => !deepEqual(state, initial), [state, initial]);
  const isValid = !hasErrors(errors);

  const toMutationInput = useCallback(
    (organizationId: string): UpdateOrganizationSsoInput => ({
      organizationId,
      enabled: state.enabled,
      enforced: state.enforced,
      emailDomains: state.emailDomains,
      google: state.google.enabled
        ? {
            hostedDomain: state.google.hostedDomain.trim().toLowerCase(),
            allowJIT: state.google.allowJIT,
          }
        : null,
      microsoft: state.microsoft.enabled
        ? {
            tenantId: state.microsoft.tenantId.trim().toLowerCase(),
            allowJIT: state.microsoft.allowJIT,
          }
        : null,
    }),
    [state],
  );

  return { state, errors, isDirty, isValid, dispatch, toMutationInput };
}
